import { PushNotificationSchema } from '@capacitor/push-notifications';
import { environment } from '@environments/environment';
import { AppConfig } from '@model/app-config';
import { DeviceInfo } from '@model/device-info';
import { Event } from '@model/event';
import { Participant } from '@model/participant';
import {
  PushNotification,
  PushNotificationData,
} from '@model/push-notification';

const pushTopicPrefix = environment.production ? '' : `${environment.name}.`;

/**
 * Creates the generic notifications topic for an event.
 *
 * The topic uses the pattern `{environmentPrefix}{appConfigCode}.notifications`.
 *
 * @param appConfig AppConfig for which the notifications topic should be created.
 * @returns The notifications topic or `undefined` when no appConfig is provided.
 */
export const createNotificationsPushTopic = (
  appConfig?: AppConfig,
): string | undefined =>
  appConfig ? `${pushTopicPrefix}${appConfig.code}.notifications` : undefined;

/**
 * Creates a participant-specific push topic for an event.
 *
 * The topic uses the pattern `{environmentPrefix}{eventCode}.P.{participantUuid}`.
 *
 * @param participant Participant to create the topic for.
 * @param event Event to which the participant belongs.
 * @returns The participant topic or `undefined` when no event is provided.
 */
export const createParticipantPushTopic = (
  participant: Participant,
  event?: Event,
): string | undefined =>
  event ? `${pushTopicPrefix}${event.code}.P.${participant.uuid}` : undefined;

/**
 * Creates a debug push topic for a specific device within an event.
 *
 * The topic uses the pattern `{environmentPrefix}{appConfigCode}.debug.{deviceIdentifier}`.
 *
 * @param deviceInfo Device information containing the unique identifier.
 * @param appConfig AppConfig context for the debug topic.
 * @returns The debug topic or `undefined` when no appConfig is provided.
 */
export const createDebugPushTopic = (
  deviceInfo: DeviceInfo,
  appConfig?: AppConfig,
): string | undefined =>
  appConfig
    ? `${pushTopicPrefix}${appConfig.code}.debug.${deviceInfo.identifier}`
    : undefined;

/**
 * Creates a live push topic for an event.
 *
 * The topic uses the pattern `{environmentPrefix}{appConfigCode}.live`.
 *
 * @param appConfig AppConfig context for the live topic.
 * @returns The live topic or `undefined` when no appConfig is provided.
 */
export const createLivePushTopic = (
  appConfig?: AppConfig,
): string | undefined =>
  appConfig ? `${pushTopicPrefix}${appConfig.code}.live` : undefined;

/**
 * Creates a PushNotification object from a PushNotificationSchema.
 *
 * This function extracts and normalizes push notification data from the schema,
 * handling platform-specific differences between iOS and Android:
 * - iOS: notification data is nested inside `aps.data`
 * - Android: notification data is at the root level
 *
 * @param pushNotificationSchema The push notification schema received from Capacitor.
 * @returns A normalized PushNotification object, or `undefined` if the schema contains no data.
 */
export const createPushNotificationFromSchema = (
  pushNotificationSchema: PushNotificationSchema,
): PushNotification | undefined => {
  const pushNotificationSchemaData = pushNotificationSchema.data as
    | PushNotificationData
    | undefined;

  if (!pushNotificationSchemaData) {
    return;
  }

  const pushNotificationData = pushNotificationSchemaData.aps
    ? pushNotificationSchemaData.aps.data
    : pushNotificationSchemaData;

  const pushNotification: PushNotification = {
    ...pushNotificationSchema,
    ...pushNotificationData,
  };

  return pushNotification;
};
